/**
 * The dashboard's translations: one i18next instance, started in the language
 * the store in `./language` holds (Dutch on a first visit) and switched with
 * it, so every `useTranslation()` re-renders on a switch without a reload.
 *
 * Components translate with `useTranslation()` from react-i18next; shared
 * helpers outside React (`@/lib/connector-status`, `@/lib/usage-window`, …)
 * use `i18n.t` and keep their signatures. The texts live in `./locales`,
 * Dutch first: English and French are typed against it.
 *
 * `{{count, number}}` in a text writes the number the dashboard's way
 * (`formatNumber`): "1.234" in Dutch and French, "1,234" in English.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { formatNumber } from "@/lib/format";
import { logger } from "@/lib/logger";

import {
  DEFAULT_LANGUAGE,
  getLanguage,
  isLanguage,
  LANGUAGES,
  subscribeLanguage,
} from "./language";
import { en } from "./locales/en";
import { fr } from "./locales/fr";
import { nl } from "./locales/nl";

/** Every language's texts, in i18next's one namespace. */
export const resources = {
  nl: { translation: nl },
  en: { translation: en },
  fr: { translation: fr },
};

void i18n.use(initReactI18next).init({
  resources,
  lng: getLanguage(),
  // Never reached while every language is complete (the locale test checks).
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...LANGUAGES],
  // The texts are bundled, so the instance is ready as soon as this returns.
  initAsync: false,
  returnNull: false,
  // React escapes what it renders.
  interpolation: { escapeValue: false },
});

// i18next's own number format would write French "1 234"; De Ceuster writes "1.234".
i18n.services.formatter?.add("number", (value: unknown, lng: string | undefined) =>
  formatNumber(Number(value), {}, isLanguage(lng) ? lng : getLanguage()),
);

// Registered before any component subscribes, so i18next has switched by the
// time a component re-renders for the new language.
subscribeLanguage(() => {
  const language = getLanguage();
  logger.debug("[i18n]", "language", language);
  void i18n.changeLanguage(language);
});

export { i18n };
